<template>
  <Teleport to="body">
    <Transition name="allocation-drawer">
      <aside v-if="modelValue && selection" class="allocation-drawer" role="dialog" aria-modal="false" :aria-labelledby="titleId">
        <header class="allocation-drawer__header">
          <div>
            <p class="allocation-drawer__eyebrow">Capacity allocation</p>
            <h2 :id="titleId" class="allocation-drawer__title">{{ selection.vendorName }} / {{ selection.typeLabel }}</h2>
            <span
              v-if="isManual"
              class="mt-1 inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700"
              title="This allocation was entered by hand, not imported from a capacity plan"
            >
              Manually created
            </span>
          </div>
          <button
            type="button"
            class="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
            aria-label="Close allocation details"
            @click="close"
          >
            <v-icon icon="mdi-close" size="20" />
          </button>
        </header>

        <div class="allocation-drawer__body">
          <section class="allocation-drawer__summary" aria-label="Allocation context">
            <div>
              <span>Week starting</span>
              <strong>{{ formatWeek(selection.allocation.weekKey) }}</strong>
            </div>
          </section>

          <div class="allocation-drawer__workspace">
            <v-window v-model="activeTab" class="allocation-drawer__workspace-content">
              <v-window-item value="purchase-orders">
                <template v-if="!assignMode">
                  <div class="allocation-drawer__section-heading" style="margin-top: 1rem">
                    <div>
                      <h3>Scheduled purchase orders</h3>
                    </div>
                    <div class="flex items-center gap-1">
                      <button
                        v-if="assignContext"
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                        @click="assignMode = true"
                      >
                        <v-icon icon="mdi-plus" size="16" />
                        Schedule
                      </button>
                    </div>
                  </div>
                  <p v-if="(props.selection?.allocation.purchaseOrders ?? []).length === 0" class="allocation-drawer__empty">
                    No purchase orders scheduled this week.
                  </p>
                  <div v-else class="allocation-drawer__po-list">
                    <div
                      v-for="purchaseOrder in props.selection?.allocation.purchaseOrders ?? []"
                      :key="purchaseOrder.id ?? purchaseOrder.poNumber ?? ''"
                      class="allocation-drawer__po-entry"
                      :class="{ 'allocation-drawer__po-entry--expanded': isExpanded(purchaseOrder) }"
                    >
                      <div class="allocation-drawer__po-item">
                        <button type="button" class="allocation-drawer__po-select" :aria-expanded="isExpanded(purchaseOrder)" @click="selectAssignment(purchaseOrder)">
                          <strong>
                            <span class="allocation-drawer__po-name" :title="assignmentLabel(purchaseOrder)">{{ assignmentLabel(purchaseOrder) }}</span>
                            <span v-if="purchaseOrder.type === ASSIGNMENT_TYPE.manual" class="allocation-drawer__po-category allocation-drawer__po-category--manual">Manual</span>
                            <span
                              v-if="purchaseOrder.productCategory"
                              class="allocation-drawer__po-category"
                              :style="{ background: categoryTagFor(purchaseOrder.productCategory).background, color: categoryTagFor(purchaseOrder.productCategory).color }"
                            >
                              {{ categoryTagFor(purchaseOrder.productCategory).label }}
                            </span>
                          </strong>
                          <span class="allocation-drawer__po-meta">
                            <span v-if="purchaseOrder.quantity === 0">0 · not counted</span>
                            <span v-else>{{ formatQuantity(purchaseOrder.quantity) }}</span>
                            <v-icon class="allocation-drawer__po-chevron" icon="mdi-chevron-down" size="18" />
                          </span>
                        </button>
                        <button
                          v-if="purchaseOrder.id"
                          type="button"
                          class="allocation-drawer__po-remove"
                          :aria-label="`Remove ${assignmentLabel(purchaseOrder)} from this allocation`"
                          @click.stop="requestRemoveAssignment(purchaseOrder)"
                        >
                          <v-icon icon="mdi-trash-can-outline" size="16" />
                        </button>
                      </div>

                      <section v-if="isExpanded(purchaseOrder) && selectedPurchaseOrder" class="allocation-drawer__po-detail">
                        <div v-if="hasSelectedParameters" class="allocation-drawer__parameter-grid">
                          <div v-if="selectedIsNonCapacity">
                            <span>Quantity</span>
                            <strong>{{ formatQuantity(selectedPurchaseOrder.quantity) }}</strong>
                          </div>
                          <div v-if="selectedIsManual && selectedPurchaseOrder.poNumber">
                            <span>References</span>
                            <strong>{{ selectedPurchaseOrder.poNumber }}</strong>
                          </div>
                        </div>
                        <p v-if="selectedIsNonCapacity" class="allocation-drawer__help">
                          {{ selectedPurchaseOrder.productCategory }} PO — planned into this week without consuming {{ selection.typeLabel }} capacity.
                        </p>
                        <div v-else class="allocation-drawer__po-qty-edit">
                          <label :for="`drawer-qty-${selectedPurchaseOrder.id}`">Quantity</label>
                          <input
                            :id="`drawer-qty-${selectedPurchaseOrder.id}`"
                            v-model.number="editedQuantity"
                            type="number"
                            min="0"
                            class="allocation-drawer__po-qty-input"
                          />
                          <button type="button" class="allocation-drawer__po-qty-save" :disabled="!canSaveQuantity || savingQuantity" @click="saveQuantity">Save</button>
                          <p v-if="selectedPurchaseOrder.distributionGroupId" class="allocation-drawer__po-hint">
                            Distributed PO — this changes its {{ selection.typeLabel }} portion only; the other capacity types keep theirs.
                          </p>
                          <p v-else-if="selectedIsManual && editedQuantity === 0" class="allocation-drawer__po-hint">Not counted toward capacity — enter a quantity to count it.</p>
                        </div>
                        <div v-if="showsSelectedNote" class="allocation-drawer__note">
                          <span>Planning notes</span>
                          <p>{{ selectedPurchaseOrder.notes }}</p>
                        </div>
                      </section>
                    </div>
                  </div>
                </template>
                <PoAssignmentPicker
                  v-else-if="assignContext"
                  :vendor-id="assignContext.vendorId"
                  :capacity-type-id="assignContext.capacityTypeId"
                  :type-code="assignContext.typeCode"
                  :category="assignContext.category"
                  :row-id="selection.rowId"
                  :week-key="selection.allocation.weekKey"
                  :remaining-capacity="selection.allocation.remainedCapacity"
                  :assigned-po-numbers="selection.allocation.purchaseOrders.flatMap((po) => (po.type !== ASSIGNMENT_TYPE.manual && po.poNumber ? [po.poNumber] : []))"
                  @close="assignMode = false"
                />
              </v-window-item>

              <v-window-item value="parameters">
                <section v-if="!editingParameters" class="allocation-drawer__parameter-panel">
                  <div class="allocation-drawer__section-heading">
                    <div>
                      <p class="allocation-drawer__eyebrow">Planning capacity</p>
                      <h3>Allocation parameters</h3>
                    </div>
                    <button
                      type="button"
                      class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                      @click="editingParameters = true"
                    >
                      <v-icon icon="mdi-pencil" size="16" />
                      Edit
                    </button>
                  </div>

                  <div class="allocation-drawer__parameter-grid">
                    <div>
                      <span>Capacity</span>
                      <strong>{{ formatQuantity(derivedCapacity) }}</strong>
                    </div>
                    <div>
                      <span>Remaining</span>
                      <strong>{{ formatQuantity(derivedRemaining) }}</strong>
                    </div>
                    <div>
                      <span>POs allocated</span>
                      <strong>{{ formatQuantity(allocatedQuantity) }}</strong>
                    </div>
                  </div>
                  <p class="allocation-drawer__help">Remaining capacity is capacity minus allocated PO quantities.</p>

                  <div class="allocation-drawer__section-heading">
                    <div>
                      <p class="allocation-drawer__eyebrow">Danger zone</p>
                      <h3>Delete allocation</h3>
                    </div>
                  </div>
                  <p class="allocation-drawer__help">
                    Removes this week's capacity bucket from the calendar. Allocations with assigned POs cannot be deleted — reassign the POs first.
                  </p>
                  <div class="allocation-drawer__actions" style="justify-content: flex-start">
                    <button
                      v-if="!confirmingDelete"
                      type="button"
                      class="min-h-10 rounded-md border border-red-300 px-3.5 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                      :disabled="allocatedQuantity > 0"
                      :title="allocatedQuantity > 0 ? 'This allocation has assigned POs and cannot be deleted' : undefined"
                      @click="confirmingDelete = true"
                    >
                      Delete allocation
                    </button>
                    <template v-else>
                      <button
                        type="button"
                        class="min-h-10 rounded-md px-3.5 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                        @click="confirmingDelete = false"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        class="min-h-10 rounded-md bg-red-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
                        @click="emit('deleteAllocation')"
                      >
                        Yes, delete this allocation
                      </button>
                    </template>
                  </div>
                </section>

                <form v-else class="allocation-drawer__form" @submit.prevent="saveParameters">
                  <div class="allocation-drawer__section-heading">
                    <div>
                      <p class="allocation-drawer__eyebrow">Planning capacity</p>
                      <h3>Edit allocation parameters</h3>
                    </div>
                  </div>
                  <p v-if="capacity !== null" class="allocation-drawer__help">
                    Vendor-reported capacity:
                    <strong>{{ formatQuantity(capacity) }}</strong>
                  </p>
                  <v-text-field
                    :model-value="capacityFactor"
                    label="Capacity factor"
                    type="number"
                    min="0"
                    step="0.001"
                    variant="outlined"
                    density="comfortable"
                    @update:model-value="onCapacityFactorInput"
                  />
                  <v-text-field v-model.number="planningCapacity" label="Planning capacity" type="number" min="0" variant="outlined" density="comfortable" />
                  <p class="allocation-drawer__help">
                    Planning capacity recomputes automatically as capacity × factor whenever the factor changes above — {{ capacity !== null ? formatQuantity(capacity) : '—' }} ×
                    {{ capacityFactor }} = {{ formatQuantity(planningCapacity) }}. You can still type a direct override into planning capacity itself.
                  </p>
                  <p class="allocation-drawer__help">
                    Remaining capacity updates automatically: {{ formatQuantity(planningCapacity) }} − {{ formatQuantity(allocatedQuantity) }} allocated =
                    {{ formatQuantity(planningCapacity - allocatedQuantity) }}.
                  </p>
                  <div class="allocation-drawer__actions">
                    <button
                      class="min-h-10 rounded-md px-3.5 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                      type="button"
                      @click="editingParameters = false"
                    >
                      Cancel
                    </button>
                    <button
                      class="inline-flex min-h-10 items-center justify-center rounded-md bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                      type="submit"
                      :disabled="!isParametersValid"
                    >
                      Save parameters
                    </button>
                  </div>
                </form>
              </v-window-item>
            </v-window>

            <nav class="allocation-drawer__tab-rail" aria-label="Allocation details">
              <v-tooltip location="left">
                <template #activator="{ props: tooltipProps }">
                  <button
                    v-bind="tooltipProps"
                    type="button"
                    class="allocation-drawer__tab-button"
                    :class="{ 'allocation-drawer__tab-button--active': activeTab === 'purchase-orders' }"
                    aria-label="Purchase orders"
                    @click="activeTab = 'purchase-orders'"
                  >
                    <v-icon icon="mdi-format-list-bulleted" size="20" />
                  </button>
                </template>
                Purchase orders
              </v-tooltip>
              <v-tooltip location="left">
                <template #activator="{ props: tooltipProps }">
                  <button
                    v-bind="tooltipProps"
                    type="button"
                    class="allocation-drawer__tab-button"
                    :class="{ 'allocation-drawer__tab-button--active': activeTab === 'parameters' }"
                    aria-label="Allocation parameters"
                    @click="activeTab = 'parameters'"
                  >
                    <v-icon icon="mdi-tune-variant" size="20" />
                  </button>
                </template>
                Allocation parameters
              </v-tooltip>
            </nav>
          </div>
        </div>
      </aside>
    </Transition>
  </Teleport>

  <ConfirmDialog
    :open="pendingRemoval !== null"
    title="Remove purchase order?"
    :message="removalConfirmMessage"
    confirm-label="Remove"
    @confirm="confirmRemoveAssignment"
    @cancel="pendingRemoval = null"
  />

  <ConfirmDialog
    :open="pendingQuantityOverbook !== null"
    title="Overbook this week?"
    :message="`This will overbook this week by ${formatQuantity(pendingQuantityOverbook ?? 0)} — this may cause problems. Save anyway?`"
    confirm-label="Save anyway"
    @confirm="saveQuantityConfirmed"
    @cancel="pendingQuantityOverbook = null"
  />
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import PoAssignmentPicker from '@/components/capacity/PoAssignmentPicker.vue';
  import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
  import { ASSIGNMENT_TYPE } from '@/types/capacityCalendar';
  import type { AllocationParametersUpdate, PoAllocation, SelectedAllocation } from '@/types/capacityCalendar';
  import { assignmentLabel, computePlanningCapacity, resolveDistributionTypeCodes } from '@/utils/capacityCalendar';
  import { categoryTagFor } from '@/utils/categoryTags';

  const store = useCapacityCalendarStore();

  const titleId = 'capacity-allocation-drawer-title';

  const props = defineProps<{
    modelValue: boolean;
    selection: SelectedAllocation | null;
    selectedPoNumber: string | null;
    // A manual block has no stable poNumber (it may carry none, or share one with the real
    // PO it references), so its selection travels by assignment id in the route's ?manual=.
    selectedManualId: string | null;
  }>();

  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    saveParameters: [update: AllocationParametersUpdate];
    selectPo: [poNumber: string];
    selectManual: [assignmentId: string];
    deleteAllocation: [];
    removeAssignment: [assignmentId: string];
  }>();

  const activeTab = ref('purchase-orders');
  const capacity = ref<number | null>(null);
  const planningCapacity = ref(0);
  const capacityFactor = ref(1);
  const editingParameters = ref(false);
  const confirmingDelete = ref(false);
  const assignMode = ref(false);
  const pendingRemoval = ref<PoAllocation | null>(null);
  const editedQuantity = ref(0);
  const pendingQuantityOverbook = ref<number | null>(null);
  const savingQuantity = ref(false);

  const isManual = computed(() => props.selection?.allocation.source === 'manual');
  const assignContext = computed(() => {
    const selection = props.selection;
    return selection?.vendorId && selection?.capacityTypeId && selection?.typeCode
      ? { vendorId: selection.vendorId, capacityTypeId: selection.capacityTypeId, typeCode: selection.typeCode, category: selection.category }
      : null;
  });

  const selectedPurchaseOrder = computed(() => {
    const purchaseOrders = props.selection?.allocation.purchaseOrders ?? [];
    if (props.selectedManualId) {
      return purchaseOrders.find((po) => po.id === props.selectedManualId) ?? null;
    }
    // Restricted to real PO rows: a manual block referencing the same number is a separate
    // entry and must not answer to the PO's selection.
    return props.selectedPoNumber ? (purchaseOrders.find((po) => po.type !== ASSIGNMENT_TYPE.manual && po.poNumber === props.selectedPoNumber) ?? null) : null;
  });

  const selectedIsManual = computed(() => selectedPurchaseOrder.value?.type === ASSIGNMENT_TYPE.manual);

  // productCategory is only ever set on a PO with no Finished Goods lines, so a real PO row
  // carrying one is planning-only and gets no quantity editor. A manual block is exempt: the
  // category describes the PO it references, while the block itself does consume capacity.
  const selectedIsNonCapacity = computed(() => !selectedIsManual.value && !!selectedPurchaseOrder.value?.productCategory);

  // The grid is a flex item, so rendering it with every field absent leaves a blank band
  // above the quantity editor.
  const hasSelectedParameters = computed(() => selectedIsNonCapacity.value || (selectedIsManual.value && !!selectedPurchaseOrder.value?.poNumber));

  // A manual block's row label is its note (via assignmentLabel), truncated to one line —
  // notes run to 500 characters, so the expanded detail is the only place to read one in full.
  const showsSelectedNote = computed(() => !!selectedPurchaseOrder.value?.notes);

  // Rows carry no id until they are persisted; two such rows must not both read as expanded.
  function isExpanded(purchaseOrder: PoAllocation): boolean {
    return !!purchaseOrder.id && purchaseOrder.id === selectedPurchaseOrder.value?.id;
  }

  function selectAssignment(purchaseOrder: PoAllocation): void {
    if (purchaseOrder.type === ASSIGNMENT_TYPE.manual) {
      if (purchaseOrder.id) emit('selectManual', purchaseOrder.id);
      return;
    }
    if (purchaseOrder.poNumber) selectPo(purchaseOrder.poNumber);
  }
  const isParametersValid = computed(() => [planningCapacity.value, capacityFactor.value].every(Number.isFinite) && planningCapacity.value >= 0 && capacityFactor.value >= 0);
  const allocatedQuantity = computed(() => props.selection?.allocation.purchaseOrders.reduce((total, po) => total + po.quantity, 0) ?? 0);
  const derivedRemaining = computed(() => props.selection?.allocation.remainedCapacity ?? 0);
  const derivedCapacity = computed(() => props.selection?.allocation.planningCapacity ?? 0);
  const canSaveQuantity = computed(
    () =>
      selectedPurchaseOrder.value?.id !== undefined &&
      Number.isFinite(editedQuantity.value) &&
      editedQuantity.value >= 0 &&
      editedQuantity.value !== selectedPurchaseOrder.value.quantity
  );

  watch(
    () => props.selection,
    (selection) => {
      // Reset local UI state on every selection change, including a
      // transition to null (deselect) — otherwise flows like the removal
      // confirm dialog can strand themselves open with nothing to act on.
      editingParameters.value = false;
      confirmingDelete.value = false;
      pendingRemoval.value = null;
      pendingQuantityOverbook.value = null;
      if (!selection) {
        return;
      }
      capacity.value = selection.allocation.capacity ?? null;
      planningCapacity.value = selection.allocation.planningCapacity ?? allocationTotal(selection.allocation.purchaseOrders, selection.allocation.remainedCapacity);
      capacityFactor.value = selection.allocation.capacityFactor ?? 1;
    },
    { immediate: true }
  );

  // Keyed on the cell's identity, not the selection object: scheduling swaps in a fresh
  // selection for the same cell, and resetting there would close the form the user is
  // still working in. Only moving to a different cell (or closing) exits schedule mode.
  watch(
    () => (props.selection ? `${props.selection.rowId}|${props.selection.allocation.weekKey}` : null),
    () => {
      assignMode.value = false;
    }
  );

  watch(
    () => selectedPurchaseOrder.value,
    (po) => {
      editedQuantity.value = po?.quantity ?? 0;
      pendingQuantityOverbook.value = null;
    },
    { immediate: true }
  );

  const removalConfirmMessage = computed(() => {
    const purchaseOrder = pendingRemoval.value;
    const poNumber = purchaseOrder?.poNumber ?? '';
    const genericMessage = `${poNumber} was distributed across this week's capacity types — removing it removes all portions.`;
    const vendorId = props.selection?.vendorId;
    const weekKey = props.selection?.allocation.weekKey;
    const distributionGroupId = purchaseOrder?.distributionGroupId;
    if (!vendorId || !weekKey || !distributionGroupId) {
      return genericMessage;
    }

    const codes = resolveDistributionTypeCodes({ rows: store.rows, allocations: store.allocations, vendorId, weekKey, distributionGroupId });

    if (codes.length < 2) {
      return genericMessage;
    }
    return `${poNumber} was distributed across ${codes.join(', ')} for this week — removing it removes all portions.`;
  });

  function allocationTotal(purchaseOrders: { quantity: number }[], remaining: number): number {
    return purchaseOrders.reduce((total, po) => total + po.quantity, 0) + Math.max(remaining, 0);
  }

  function formatWeek(weekKey: string): string {
    const [year, month, day] = weekKey.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function close(): void {
    pendingRemoval.value = null;
    emit('update:modelValue', false);
  }

  function selectPo(poNumber: string): void {
    emit('selectPo', poNumber);
  }

  function requestRemoveAssignment(purchaseOrder: PoAllocation): void {
    if (!purchaseOrder.id) {
      return;
    }
    if (purchaseOrder.distributionGroupId) {
      pendingRemoval.value = purchaseOrder;
      return;
    }
    emit('removeAssignment', purchaseOrder.id);
  }

  function confirmRemoveAssignment(): void {
    const purchaseOrder = pendingRemoval.value;
    pendingRemoval.value = null;
    if (purchaseOrder?.id) {
      emit('removeAssignment', purchaseOrder.id);
    }
  }

  // Mirrors the backend's import-time formula, so the factor visibly drives remaining
  // capacity here too — without this, editing the factor alone left planning capacity
  // (and therefore remaining capacity) completely unchanged. Only recomputes when the raw
  // capacity is known; otherwise there's nothing to scale, so planningCapacity is left as
  // whatever the user already has (including a direct manual override of that field).
  function onCapacityFactorInput(value: string | number): void {
    const factor = Number(value);
    if (!Number.isFinite(factor)) return;
    capacityFactor.value = factor;
    const recomputed = computePlanningCapacity(capacity.value, factor);
    if (recomputed !== null) planningCapacity.value = recomputed;
  }

  function saveParameters(): void {
    if (!isParametersValid.value) {
      return;
    }
    emit('saveParameters', {
      planningCapacity: planningCapacity.value,
      capacityFactor: capacityFactor.value,
    });
    editingParameters.value = false;
  }

  async function performSaveQuantity(): Promise<void> {
    const purchaseOrder = selectedPurchaseOrder.value;
    const selection = props.selection;
    if (!purchaseOrder?.id || !selection) return;
    savingQuantity.value = true;
    try {
      await store.updatePoAssignmentQuantity(selection.rowId, selection.allocation.weekKey, purchaseOrder.id, editedQuantity.value);
    } finally {
      savingQuantity.value = false;
    }
  }

  // Mirrors the picker's overbook confirmation: the backend allows overbooking a week's
  // capacity, but only because the frontend gets explicit confirmation first — a silent
  // PATCH here would break that contract.
  async function saveQuantity(): Promise<void> {
    const purchaseOrder = selectedPurchaseOrder.value;
    if (!purchaseOrder?.id) return;
    const delta = editedQuantity.value - purchaseOrder.quantity;
    const remainedCapacity = props.selection?.allocation.remainedCapacity ?? 0;
    // Only increases need confirmation — lowering a quantity in an already-overbooked
    // week reduces the deficit and must not prompt.
    if (delta > 0 && delta > remainedCapacity) {
      pendingQuantityOverbook.value = delta - remainedCapacity;
      return;
    }
    await performSaveQuantity();
  }

  async function saveQuantityConfirmed(): Promise<void> {
    pendingQuantityOverbook.value = null;
    await performSaveQuantity();
  }

  function formatQuantity(value: number): string {
    return value.toLocaleString();
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (!props.modelValue || event.key !== 'Escape') {
      return;
    }
    // Nested-modal Escape priority: dismiss a confirm dialog first without
    // closing the drawer underneath it; a second Escape then closes the drawer.
    if (pendingRemoval.value !== null) {
      pendingRemoval.value = null;
      return;
    }
    if (pendingQuantityOverbook.value !== null) {
      pendingQuantityOverbook.value = null;
      return;
    }
    close();
  }

  onMounted(() => window.addEventListener('keydown', handleKeydown));
  onBeforeUnmount(() => window.removeEventListener('keydown', handleKeydown));
</script>

<style scoped>
  .allocation-drawer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 60;
    display: flex;
    width: min(38rem, 100vw);
    flex-direction: column;
    border-left: 1px solid rgb(203 213 225);
    background: white;
    box-shadow: -8px 0 24px rgb(15 23 42 / 12%);
  }

  .allocation-drawer__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid rgb(226 232 240);
  }

  .allocation-drawer__eyebrow {
    margin: 0 0 0.125rem;
    color: rgb(100 116 139);
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .allocation-drawer__title {
    margin: 0;
    color: rgb(15 23 42);
    font-size: 1.25rem;
    font-weight: 700;
  }

  .allocation-drawer__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 1.5rem;
    overflow-y: auto;
    padding: 1.5rem;
  }

  .allocation-drawer__summary {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .allocation-drawer__summary div {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.25rem;
  }

  .allocation-drawer__summary span {
    color: rgb(100 116 139);
    font-size: 0.6875rem;
  }

  .allocation-drawer__summary strong {
    overflow: hidden;
    color: rgb(30 41 59);
    font-size: 0.875rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .allocation-drawer__workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 3.5rem;
    flex: 1;
    min-height: 0;
    margin: 0 -1.5rem -1.5rem;
    border-top: 1px solid rgb(226 232 240);
  }

  .allocation-drawer__workspace-content {
    min-width: 0;
    overflow-y: auto;
    padding: 1.25rem 1.5rem 1.5rem;
  }

  .allocation-drawer__tab-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 0.25rem;
    border-left: 1px solid rgb(226 232 240);
    background: rgb(248 250 252);
  }

  .allocation-drawer__tab-button {
    width: 2.5rem;
    height: 2.5rem;
    min-width: 2.5rem;
    border: 1px solid transparent;
    border-radius: 0.5rem;
    background: transparent;
    color: rgb(100 116 139);
  }

  .allocation-drawer__tab-button:hover {
    background: rgb(226 232 240);
    color: rgb(30 41 59);
  }

  .allocation-drawer__tab-button--active {
    border-color: rgb(191 219 254);
    background: rgb(219 234 254);
    color: rgb(37 99 235);
  }

  .allocation-drawer__tab-button--active:hover {
    background: rgb(219 234 254);
    color: rgb(30 64 175);
  }

  .allocation-drawer__tab-button:focus-visible {
    outline: 2px solid rgb(37 99 235);
    outline-offset: 2px;
  }

  .allocation-drawer__form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-top: 1rem;
  }

  .allocation-drawer__form h3 {
    margin: 0.5rem 0;
    color: rgb(30 41 59);
    font-size: 0.875rem;
  }

  .allocation-drawer__section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid rgb(226 232 240);
  }

  .allocation-drawer__section-heading h3 {
    margin: 0;
    color: rgb(30 41 59);
    font-size: 1rem;
  }

  .allocation-drawer__parameter-panel {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    padding-top: 1rem;
  }

  .allocation-drawer__po-detail {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem 0.875rem 1.125rem;
    border-top: 1px solid rgb(191 219 254);
    background: rgb(248 250 252);
  }

  .allocation-drawer__parameter-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.25rem 1rem;
  }

  .allocation-drawer__parameter-grid div {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.375rem;
  }

  .allocation-drawer__parameter-grid span {
    color: rgb(100 116 139);
    font-size: 0.75rem;
  }

  .allocation-drawer__parameter-grid strong {
    color: rgb(30 41 59);
    font-size: 1rem;
    font-weight: 600;
  }

  .allocation-drawer__note {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 0.875rem;
    border: 1px solid rgb(226 232 240);
    border-radius: 0.5rem;
    background: rgb(248 250 252);
  }

  .allocation-drawer__note span {
    color: rgb(100 116 139);
    font-size: 0.75rem;
  }

  .allocation-drawer__note p {
    margin: 0;
    overflow-wrap: anywhere;
    color: rgb(51 65 85);
    font-size: 0.875rem;
    line-height: 1.5;
    white-space: pre-wrap;
  }

  .allocation-drawer__empty {
    margin: 0.75rem 0 0;
    color: rgb(100 116 139);
    font-size: 0.8125rem;
  }

  .allocation-drawer__po-list {
    display: flex;
    max-height: calc(100vh - 22rem);
    flex-direction: column;
    overflow-y: auto;
    border: 1px solid rgb(226 232 240);
    border-radius: 0.5rem;
  }

  .allocation-drawer__po-entry {
    border-bottom: 1px solid rgb(226 232 240);
  }

  .allocation-drawer__po-entry:last-child {
    border-bottom: 0;
  }

  /* The accent bar spans row and detail together, so an expanded item reads as one unit
     instead of a panel that happens to follow a highlighted row. */
  .allocation-drawer__po-entry--expanded {
    position: relative;
  }

  .allocation-drawer__po-entry--expanded::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 3px;
    border-radius: 0.375rem 0 0 0.375rem;
    background: rgb(37 99 235);
    content: '';
  }

  .allocation-drawer__po-item {
    display: flex;
    align-items: stretch;
    background: white;
  }

  .allocation-drawer__po-item:hover {
    background: rgb(248 250 252);
  }

  .allocation-drawer__po-entry--expanded .allocation-drawer__po-item {
    background: rgb(239 246 255);
  }

  .allocation-drawer__po-entry--expanded .allocation-drawer__po-select strong {
    color: rgb(30 64 175);
  }

  .allocation-drawer__po-meta {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.25rem;
  }

  .allocation-drawer__po-chevron {
    color: rgb(148 163 184);
    transition: transform 150ms ease;
  }

  .allocation-drawer__po-entry--expanded .allocation-drawer__po-chevron {
    color: rgb(37 99 235);
    transform: rotate(180deg);
  }

  .allocation-drawer__po-category--manual {
    background: rgb(226 232 240);
    color: rgb(51 65 85);
  }

  .allocation-drawer__po-select {
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.625rem 0.75rem;
    background: transparent;
    color: rgb(30 41 59);
    cursor: pointer;
    text-align: left;
  }

  .allocation-drawer__po-select strong {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.8125rem;
  }

  /* A manual block's label is its note, which runs to 500 characters — clamp it to one line
     here (the full text lives in the expanded detail) so one block cannot stretch the row. */
  .allocation-drawer__po-name {
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .allocation-drawer__po-select span {
    color: rgb(100 116 139);
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .allocation-drawer__po-remove {
    display: flex;
    align-items: center;
    padding: 0 0.625rem;
    color: rgb(148 163 184);
  }

  .allocation-drawer__po-remove:hover {
    color: rgb(220 38 38);
  }

  .allocation-drawer__po-select strong .allocation-drawer__po-category {
    padding: 0 0.375rem;
    border-radius: 9999px;
    font-size: 0.625rem;
    font-weight: 600;
    line-height: 1.5;
  }

  .allocation-drawer__po-qty-edit {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .allocation-drawer__po-qty-edit label {
    color: rgb(100 116 139);
    font-size: 0.75rem;
  }

  .allocation-drawer__po-qty-input {
    width: 6rem;
    padding: 0.375rem 0.5rem;
    border: 1px solid rgb(203 213 225);
    border-radius: 0.375rem;
    font-size: 0.875rem;
  }

  .allocation-drawer__po-qty-input:focus-visible {
    outline: 2px solid rgb(37 99 235);
    outline-offset: 2px;
  }

  .allocation-drawer__po-qty-save {
    min-height: 2.25rem;
    padding: 0.375rem 0.875rem;
    border-radius: 0.375rem;
    background: rgb(37 99 235);
    color: white;
    font-size: 0.8125rem;
    font-weight: 600;
    transition: background-color 150ms ease;
  }

  .allocation-drawer__po-qty-save:hover:not(:disabled) {
    background: rgb(29 78 216);
  }

  .allocation-drawer__po-qty-save:disabled {
    background: rgb(226 232 240);
    color: rgb(148 163 184);
    cursor: not-allowed;
  }

  .allocation-drawer__po-hint {
    flex-basis: 100%;
    margin: 0;
    color: rgb(100 116 139);
    font-size: 0.75rem;
  }

  .allocation-drawer__help {
    margin: 0.25rem 0 0.75rem;
    color: rgb(100 116 139);
    font-size: 0.75rem;
    line-height: 1.4;
  }

  .allocation-drawer__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding-top: 0.5rem;
  }

  .allocation-drawer-enter-active,
  .allocation-drawer-leave-active {
    transition: transform 180ms ease;
  }

  .allocation-drawer-enter-from,
  .allocation-drawer-leave-to {
    transform: translateX(100%);
  }

  @media (max-width: 640px) {
    .allocation-drawer {
      width: 100vw;
    }
  }
</style>
